import React, { useState, useEffect } from 'react';
import { Search, Plus, ChevronDown, MoreVertical, DollarSign, CreditCard, Receipt, TrendingUp, Calendar } from 'lucide-react';
import { expensesApi } from '../api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewExpense, setShowNewExpense] = useState(false);
  const [newExpenseForm, setNewExpenseForm] = useState({
    description: '',
    amount: '',
    category: 'Other',
    vendor: '',
    payment_method: 'Credit Card'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [expensesRes, summaryRes, categoriesRes] = await Promise.all([
        expensesApi.getAll(),
        expensesApi.getSummary(),
        expensesApi.getCategories()
      ]);
      setExpenses(expensesRes.data);
      setSummary(summaryRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Failed to fetch expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateExpense = async () => {
    try {
      await expensesApi.create({
        description: newExpenseForm.description,
        amount: parseFloat(newExpenseForm.amount),
        category: newExpenseForm.category,
        vendor: newExpenseForm.vendor,
        payment_method: newExpenseForm.payment_method
      });
      setShowNewExpense(false);
      setNewExpenseForm({ description: '', amount: '', category: 'Other', vendor: '', payment_method: 'Credit Card' });
      fetchData();
    } catch (error) {
      console.error('Failed to create expense:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Office Supplies': '#0077c5',
      'Utilities': '#00a19b',
      'Rent': '#6b7280',
      'Travel': '#f59e0b',
      'Meals & Entertainment': '#ef4444',
      'Equipment': '#8b5cf6',
      'Software & Subscriptions': '#2ca01c',
      'Marketing': '#ec4899',
      'Professional Services': '#0ea5e9',
      'Payroll': '#14b8a6',
      'Taxes': '#f97316',
      'Insurance': '#6366f1',
      'Other': '#9ca3af'
    };
    return colors[category] || '#9ca3af';
  };

  return (
    <div className="flex-1 bg-white overflow-auto">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-medium text-gray-800">Expenses</h1>
            <p className="text-sm text-gray-500">Track and manage your business expenses</p>
          </div>
          <button
            className="px-4 py-2 bg-[#2ca01c] text-white rounded text-sm hover:bg-[#249017] flex items-center gap-2"
            onClick={() => setShowNewExpense(true)}
          >
            <Plus className="w-4 h-4" />
            New expense
          </button>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-5 h-5 text-blue-600" />
                <span className="text-sm text-blue-700">Total Expenses</span>
              </div>
              <p className="text-2xl font-bold text-blue-800">{formatCurrency(summary.total)}</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <Receipt className="w-5 h-5 text-green-600" />
                <span className="text-sm text-green-700">This Month</span>
              </div>
              <p className="text-2xl font-bold text-green-800">{formatCurrency(summary.total * 0.3)}</p>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-orange-600" />
                <span className="text-sm text-orange-700">Categories</span>
              </div>
              <p className="text-2xl font-bold text-orange-800">{Object.keys(summary.by_category || {}).length}</p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-5 h-5 text-purple-600" />
                <span className="text-sm text-purple-700">Total Entries</span>
              </div>
              <p className="text-2xl font-bold text-purple-800">{expenses.length}</p>
            </div>
          </div>
        )}

        {/* Expense Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2ca01c]"></div>
          </div>
        ) : (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">DATE</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">DESCRIPTION</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">CATEGORY</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">VENDOR</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">PAYMENT</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">AMOUNT</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((expense) => (
                  <tr key={expense.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {formatDate(expense.expense_date)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                      {expense.description}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="px-2 py-1 rounded-full text-xs font-medium text-white"
                        style={{ backgroundColor: getCategoryColor(expense.category) }}
                      >
                        {expense.category}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {expense.vendor || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {expense.payment_method}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 font-medium text-right">
                      {formatCurrency(expense.amount)}
                    </td>
                    <td className="px-4 py-3">
                      <button className="p-1 text-gray-400 hover:text-gray-600">
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* New Expense Modal */}
      <Dialog open={showNewExpense} onOpenChange={setShowNewExpense}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Record New Expense</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="description">Description *</Label>
              <Input
                id="description"
                value={newExpenseForm.description}
                onChange={(e) => setNewExpenseForm({ ...newExpenseForm, description: e.target.value })}
                placeholder="What did you spend on?"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="amount">Amount *</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  value={newExpenseForm.amount}
                  onChange={(e) => setNewExpenseForm({ ...newExpenseForm, amount: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="category">Category</Label>
                <select
                  id="category"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={newExpenseForm.category}
                  onChange={(e) => setNewExpenseForm({ ...newExpenseForm, category: e.target.value })}
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="vendor">Vendor</Label>
                <Input
                  id="vendor"
                  value={newExpenseForm.vendor}
                  onChange={(e) => setNewExpenseForm({ ...newExpenseForm, vendor: e.target.value })}
                  placeholder="Who did you pay?"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="payment">Payment Method</Label>
                <select
                  id="payment"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={newExpenseForm.payment_method}
                  onChange={(e) => setNewExpenseForm({ ...newExpenseForm, payment_method: e.target.value })}
                >
                  <option value="Cash">Cash</option>
                  <option value="Check">Check</option>
                  <option value="Credit Card">Credit Card</option>
                  <option value="Debit Card">Debit Card</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                </select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewExpense(false)}>Cancel</Button>
            <Button className="bg-[#2ca01c] hover:bg-[#249017]" onClick={handleCreateExpense}>Save Expense</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Expenses;